import React, {useEffect, useRef, useState} from 'react';
import Webcam from 'react-webcam';
import {Button, Modal} from "antd";
import {Header} from "../../styles";
import {IconContext} from "react-icons";
import {FaArrowLeft} from "react-icons/fa";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {DashboardWrapper} from "../../../../components/Layout/Layout.styles";
import {GeneralBody} from "../../../../widgets/GeneralBody";
import {uploadPatientImage} from "../../../../utils/api-lists";
import EllipseOverlay from "./ellipse-overlay.component";
import {RotateRightOutlined, SyncOutlined} from "@ant-design/icons";
import faceFrontImg from "../../../../assets/no-glasses 1-1.png";
import faceRightImg from "../../../../assets/no-glasses 1-2.png";
import faceLeftImg from "../../../../assets/no-glasses 1-3.png";
import faceSmileFrontImg from "../../../../assets/no-glasses 1-4.png";
import faceSmileRightImg from "../../../../assets/no-glasses 1-5.png";
import faceSmileLeftImg from "../../../../assets/no-glasses 1-6.png";

const CameraPage = () => {
    const [capturing, setCapturing] = useState(false);
    const {patientId} = useParams();
    const [recordedChunks, setRecordedChunks] = useState([]);
    const [imgSrc, setImgSrc] = useState(null);
    const [mode, setMode] = useState('camera'); // 'camera' or 'preview'
    const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
    const [loading, setLoading] = useState(false);
    const [facingMode, setFacingMode] = useState('environment'); // 'user' for front camera, 'environment' for back camera
    const [mirrored, setMirrored] = useState(true); // Default to no mirroring
    const [showInstructionsModal, setShowInstructionsModal] = useState(false);

    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);

    // const getVideoDevices = React.useCallback(async () => {
    //     try {
    //         const devices = await navigator.mediaDevices.enumerateDevices();
    //         const videoInputs = devices.filter(device => device.kind === 'videoinput');
    //         console.log('Available video devices:', videoInputs);
    //         setVideoDevices(videoInputs);
    //
    //         // Select first device if none is selected yet
    //         if (videoInputs.length > 0 && !selectedDeviceId) {
    //             setSelectedDeviceId(videoInputs[0].deviceId);
    //         }
    //     } catch (error) {
    //         console.error('Error getting video devices:', error);
    //     }
    // }, [selectedDeviceId]);
    //
    // useEffect(() => {
    //     getVideoDevices();
    // }, [getVideoDevices]);

    const [feedback, setFeedback] = useState({
        face_count: 0,
        lighting_issues: [],
        alignment_issue: "",
        distance_issue: "",
        visual_feedback: null
    });

    const toggleMirroring = React.useCallback(() => {
        setMirrored(prevMirrored => !prevMirrored);
    }, []);
    const flipCamera = React.useCallback(() => {
        setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
    }, []);

    const webcamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const socketRef = useRef(null);
    const navigate = useNavigate();
    const {t} = useTranslation("common");

    const API_URL = process.env.REACT_APP_WEB_SOCKET_URL || 'ws://localhost:8765';

    // WebSocket connection
    useEffect(() => {
        socketRef.current = new WebSocket(API_URL);

        socketRef.current.onopen = () => {
            console.log('WebSocket connected');
        };

        socketRef.current.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('WebSocket data:', data);
                setFeedback(data); // Store feedback data
            } catch (error) {
                console.error('Error parsing WebSocket data:', error);
            }
        };

        // Send webcam image every 0.5 seconds
        const intervalId = setInterval(() => {
            if (socketRef.current &&
                socketRef.current.readyState === WebSocket.OPEN &&
                webcamRef.current &&
                mode === 'camera') {

                // Capture screenshot from webcam
                const screenshot = webcamRef.current.getScreenshot();

                // Send the image data through WebSocket
                if (screenshot) {
                    socketRef.current.send(JSON.stringify({
                        type: 'image',
                        timestamp: new Date().toISOString(),
                        image: screenshot
                    }));
                }
            }
        }, 500);

        return () => {
            clearInterval(intervalId);
            if (socketRef.current) socketRef.current.close();
        };
    }, [mode]);

    useEffect(() => {
        if (webcamRef.current && webcamRef.current.video) {
            const logVideoDetails = () => {
                const video = webcamRef.current.video;
                console.log('Actual video resolution:', {
                    width: video.videoWidth,
                    height: video.videoHeight
                });
            };

            // Log details once video is playing
            webcamRef.current.video.addEventListener('playing', logVideoDetails);

            return () => {
                if (webcamRef.current && webcamRef.current.video) {
                    webcamRef.current.video.removeEventListener('playing', logVideoDetails);
                }
            };
        }
    }, []);

    // Update capturePhoto function to ensure highest quality
    const capturePhoto = React.useCallback(() => {
        if (webcamRef.current) {
            // Force highest quality screenshot
            const options = {
                width: webcamRef.current.video.videoWidth,
                height: webcamRef.current.video.videoHeight
            };
            const imageSrc = webcamRef.current.getScreenshot(options);
            setImgSrc(imageSrc);
            setMediaType('image');
            setMode('preview');
        }
    }, [webcamRef]);

    // Start video recording
    const startRecording = React.useCallback(() => {
        setCapturing(true);
        setMediaType('video');

        const stream = webcamRef.current.video.srcObject;
        mediaRecorderRef.current = new MediaRecorder(stream, {
            mimeType: 'video/webm'
        });
        mediaRecorderRef.current.addEventListener('dataavailable', handleDataAvailable);
        mediaRecorderRef.current.start();
    }, [webcamRef]);

    // Show instructions modal for video recording
    const showRecordingInstructions = React.useCallback(() => {
        setShowInstructionsModal(true);
    }, []);

    // Handle continue from instructions modal
    const handleContinueRecording = React.useCallback(() => {
        setShowInstructionsModal(false);
        startRecording();
    }, [startRecording]);

    // Handle cancel from instructions modal
    const handleCancelRecording = React.useCallback(() => {
        setShowInstructionsModal(false);
    }, []);

    // Stop video recording
    const stopRecording = React.useCallback(() => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
        }
        setCapturing(false);
        setTimeout(() => setMode('preview'), 100);
    }, [mediaRecorderRef]);

    const handleDataAvailable = React.useCallback(({data}) => {
        if (data.size > 0) {
            setRecordedChunks((prev) => [...prev, data]);
        }
    }, []);

    // Handle cancel button in preview mode
    const handleCancel = () => {
        setMode('camera');
        setImgSrc(null);
        setRecordedChunks([]);
    };

    // Handle upload button in preview mode
    const handleUpload = async () => {
        setLoading(true);

        try {
            let file, fileName;

            if (mediaType === 'image') {
                // Convert base64 image to file
                const response = await fetch(imgSrc);
                const blob = await response.blob();
                fileName = `patient_photo_${Date.now()}.jpg`;
                file = new File([blob], fileName, {type: 'image/jpeg'});
            } else {
                // Create file from video blob
                const blob = new Blob(recordedChunks, {type: 'video/webm'});
                fileName = `patient_video_${Date.now()}.webm`;
                file = new File([blob], fileName, {type: 'video/webm'});
            }

            // Upload the file using the API
            await uploadPatientImage(patientId, file, fileName);

            // Reset state and navigate to analysis page
            setImgSrc(null);
            setRecordedChunks([]);
            setMode('camera');

            // Navigate to the analysis page
            navigate(`/patient-cabinet/${patientId}/analysis`, {
                state: {
                    file_open_last: false,
                },
                replace: true,
            });

        } catch (error) {
            console.error('Upload error:', error);
            // You may want to show an error message here
        } finally {
            setLoading(false);
        }
    };

    // Create video preview URL
    const videoPreviewUrl = React.useMemo(() => {
        if (recordedChunks.length > 0) {
            const blob = new Blob(recordedChunks, {type: 'video/webm'});
            return URL.createObjectURL(blob);
        }
        return '';
    }, [recordedChunks]);

    // Update the videoConstraints object to request the highest quality
    const videoConstraints = {
        width: {min: 1280, ideal: 3840, max: 7680},
        height: {min: 720, ideal: 2160, max: 4320},
        // aspectRatio: 10 / 16,
        facingMode: facingMode, // Use the state instead of hardcoded value
        frameRate: {ideal: 30, max: 60},
        resizeMode: "none",
        forceScreenshotSourceSize: true
    };

    // Feedback overlay component
    const FeedbackOverlay = () => {
        // If there's no face detected, only show that message
        if (feedback.face_count === "No face detected") {
            return (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    background: 'rgba(0, 0, 0, 0.6)',
                    color: '#fff',
                    padding: '10px',
                    borderRadius: '5px',
                    maxWidth: '300px',
                    fontSize: '14px',
                    zIndex: 10
                }}>
                    <p style={{margin: '0', fontWeight: 'bold', color: '#ff4d4f'}}>No face detected</p>
                </div>
            );
        }

        return (
            <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                background: 'rgba(0, 0, 0, 0.6)',
                color: '#fff',
                padding: '10px',
                borderRadius: '5px',
                maxWidth: '300px',
                fontSize: '14px',
                zIndex: 10
            }}>
                <p style={{margin: '0', fontWeight: 'bold'}}>Face count: {feedback.face_count}</p>

                {feedback.lighting_issues && feedback.lighting_issues.length > 0 && (
                    <div>
                        <p style={{margin: '5px 0 0 0', fontWeight: 'bold', color: '#ffcb00'}}>
                            Lighting issues:
                        </p>
                        <ul style={{margin: '0', paddingLeft: '20px'}}>
                            {feedback.lighting_issues.map((issue, index) => (
                                <li key={index}>{issue}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {feedback.alignment_issue && (
                    <p style={{margin: '5px 0 0 0', color: '#ffcb00'}}>
                        <strong>Alignment:</strong> {feedback.alignment_issue}
                    </p>
                )}

                {feedback.distance_issue && (
                    <p style={{margin: '5px 0 0 0', color: '#ffcb00'}}>
                        <strong>Distance:</strong> {feedback.distance_issue}
                    </p>
                )}
            </div>
        );
    };

    return (
        <DashboardWrapper style={{height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Header>
                <IconContext.Provider value={{style: {cursor: "pointer"}}}>
                    <FaArrowLeft onClick={() => navigate(-1)}/>
                </IconContext.Provider>
                <h3>{t("visual_analysis_upload_file")}</h3>
            </Header>

            <GeneralBody style={{flex: 1, overflow: 'hidden'}}>
                <div className="camera-page" style={{
                    height: '100%',
                    maxHeight: 'calc(100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {mode === 'camera' ? (
                        <div className="camera-mode" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative'
                        }}>
                            <div style={{
                                width: '100%',
                                height: '100%',
                                maxHeight: 'calc(100% - 80px)',
                                display: 'flex',
                                justifyContent: 'center',
                                position: 'relative'
                            }}>
                                <Webcam
                                    audio={false}
                                    ref={webcamRef}
                                    screenshotFormat="image/jpeg"
                                    screenshotQuality={1}
                                    videoConstraints={videoConstraints}
                                    className="webcam"
                                    mirrored={mirrored} // Use the mirrored state instead of hardcoded false
                                    style={{
                                        height: '100%',
                                        width: 'auto',
                                        maxWidth: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                                <FeedbackOverlay/>
                                <EllipseOverlay feedback={feedback} webcamRef={webcamRef}/>
                            </div>

                            <div className="controls" style={{
                                marginTop: '20px',
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'center'
                            }}>
                                {capturing ? (
                                    <Button
                                        onClick={stopRecording}
                                        danger
                                    >
                                        Stop Recording
                                    </Button>
                                ) : (
                                    <>
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '80px',
                                            display: 'flex',
                                            gap: '10px',
                                        }}>
                                            <Button
                                                onClick={capturePhoto}
                                                type="primary"
                                            >
                                                Take Photo
                                            </Button>
                                            <Button
                                                onClick={showRecordingInstructions}
                                            >
                                                Record Video
                                            </Button>
                                        </div>

                                        <div style={{
                                            position: 'absolute',
                                            bottom: '120px',
                                            display: 'flex',
                                            gap: '10px',
                                        }}>
                                            <Button
                                                onClick={flipCamera}
                                                icon={<SyncOutlined/>}
                                            >
                                            </Button>
                                            <Button
                                                onClick={toggleMirroring}
                                                icon={<RotateRightOutlined/>}
                                            >
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="preview-mode" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '100%',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                maxHeight: 'calc(100%)',
                                overflow: 'hidden'
                            }}>
                                {mediaType === 'image' ? (
                                    <img
                                        src={imgSrc}
                                        alt="captured"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <video
                                        src={videoPreviewUrl}
                                        controls
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                )}
                            </div>

                            <div className="preview-controls" style={{
                                marginTop: '20px',
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'center'
                            }}>
                                <Button
                                    onClick={handleCancel}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUpload}
                                    type="primary"
                                    loading={loading}
                                    disabled={loading}
                                >
                                    {loading ? 'Uploading...' : 'Upload'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </GeneralBody>

            {/* Video Recording Instructions Modal */}
            <Modal
                title={t("prepare_skin_analysis")}
                open={showInstructionsModal}
                zIndex={999999}
                className="whiteModal"
                onCancel={handleCancelRecording}
                footer={[
                    <Button key="cancel" onClick={handleCancelRecording}>
                        Cancel
                    </Button>,
                    <Button key="continue" type="primary" onClick={handleContinueRecording}>
                        Continue
                    </Button>
                ]}
                width={600}
                centered
            >
                <div style={{padding: '20px 0'}}>
                    <h4 style={{marginBottom: '15px'}}>Follow these 6 steps in 1 video to capture the face:</h4>

                    <div style={{marginBottom: '20px'}}>
                        <h5 style={{color: '#666', marginBottom: '10px'}}>Neutral Face:</h5>
                        <div style={{display: 'flex', justifyContent: 'space-around', marginBottom: '15px'}}>
                            <div style={{textAlign: 'center'}}>
                                <div style={{marginBottom: '5px', display: 'flex', justifyContent: "space-around"}}>
                                    <img src={faceFrontImg} alt="Org" width={80}/>
                                </div>
                                <span style={{fontSize: '12px'}}>Front View</span>
                            </div>
                            <div style={{textAlign: 'center'}}>
                                <div style={{marginBottom: '5px', display: 'flex', justifyContent: "space-around"}}>
                                    <img src={faceRightImg} alt="Org" width={80}/>
                                </div>
                                <span style={{fontSize: '12px'}}>Looking Slightly Right</span>
                            </div>
                            <div style={{textAlign: 'center'}}>
                                <div style={{marginBottom: '5px', display: 'flex', justifyContent: "space-around"}}>
                                    <img src={faceLeftImg} alt="Org" width={80}/>
                                </div>
                                <span style={{fontSize: '12px'}}>Looking Slightly Left</span>
                            </div>
                        </div>
                    </div>

                    <div style={{marginBottom: '20px'}}>
                        <h5 style={{color: '#666', marginBottom: '10px'}}>Smiling Face:</h5>
                        <div style={{display: 'flex', justifyContent: 'space-around', marginBottom: '15px'}}>
                            <div style={{textAlign: 'center'}}>
                                <div style={{marginBottom: '5px', display: 'flex', justifyContent: "space-around"}}>
                                    <img src={faceSmileFrontImg} alt="Org" width={80}/>
                                </div>
                                <span style={{fontSize: '12px'}}>Front View</span>
                            </div>
                            <div style={{textAlign: 'center'}}>
                                <div style={{marginBottom: '5px', display: 'flex', justifyContent: "space-around"}}>
                                    <img src={faceSmileRightImg} alt="Org" width={80}/>
                                </div>
                                <span style={{fontSize: '12px'}}>Looking Slightly Right</span>
                            </div>
                            <div style={{textAlign: 'center'}}>
                                <div style={{marginBottom: '5px', display: 'flex', justifyContent: "space-around"}}>
                                    <img src={faceSmileLeftImg} alt="Org" width={80}/>
                                </div>
                                <span style={{fontSize: '12px'}}>Looking Slightly Left</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

        </DashboardWrapper>
    );
};

export default CameraPage;
